/**
 * carousel-recommendations.js
 *
 * Dawn pattern: on first page load, recommendations.performed is
 * always false (Shopify only populates it when the section is
 * rendered through the dedicated recommendations endpoint). So this
 * component fetches the section again via routes.product_recommendations_url
 * — that second render has recommendations.products populated, and
 * returns real <li class="carousel-recommendations__slide"> cards
 * built from your card-product snippet (image ratio/shape/vendor
 * settings respected). We extract those slides into the visible
 * track, then layer on carousel behavior: arrows, dots, swipe,
 * infinite loop, one product per click.
 */
class CarouselRecommendations extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slidesPerView = 1;
    this.totalSlides = 0;

    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
  }

  connectedCallback() {
    this.fetchRecommendations();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  fetchRecommendations() {
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        // The fetched response is this same section, re-rendered with
        // recommendations.performed = true. Pull the real slide <li>
        // elements straight out of it.
        const fetchedSlides = html.querySelectorAll('.carousel-recommendations__slide');

        if (!fetchedSlides || fetchedSlides.length === 0) {
          // Shopify genuinely has no recommendations for this product.
          this.remove();
          return;
        }

        this.mountSlides(fetchedSlides);
      })
      .catch(() => {
        this.remove();
      });
  }

  mountSlides(fetchedSlides) {
    const inner = this.querySelector('.carousel-recommendations__inner');
    const track = this.querySelector('.carousel-recommendations__track');

    track.innerHTML = '';
    fetchedSlides.forEach((slide) => {
      track.appendChild(slide.cloneNode(true));
    });

    this.totalSlides = track.children.length;

    if (this.totalSlides === 0) {
      this.remove();
      return;
    }

    inner.hidden = false;

    this.track = track;
    this.viewport = this.querySelector('.carousel-recommendations__viewport');
    this.prevButton = this.querySelector('.carousel-recommendations__arrow--prev');
    this.nextButton = this.querySelector('.carousel-recommendations__arrow--next');
    this.dotsWrapper = this.querySelector('.carousel-recommendations__dots');

    this.buildDots();
    this.updateSlidesPerView();
    this.bindEvents();
    this.goTo(0, false);
  }

  buildDots() {
    this.dotsWrapper.innerHTML = '';
    this.dots = [];

    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'carousel-recommendations__dot';
      dot.setAttribute('aria-label', `Go to product ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i, true));
      this.dotsWrapper.appendChild(dot);
      this.dots.push(dot);
    }
  }

  updateSlidesPerView() {
    const styles = getComputedStyle(this);
    const mobileCols = parseFloat(styles.getPropertyValue('--carousel-cols-mobile')) || 1;
    const desktopCols = parseFloat(styles.getPropertyValue('--carousel-cols-desktop')) || 4;
    this.slidesPerView = window.matchMedia('(min-width: 750px)').matches ? desktopCols : mobileCols;
  }

  bindEvents() {
    this.prevButton.addEventListener('click', this.onPrevClick);
    this.nextButton.addEventListener('click', this.onNextClick);
    window.addEventListener('resize', this.onResize);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.onNextClick();
      if (event.key === 'ArrowLeft') this.onPrevClick();
    });
  }

  onResize() {
    this.updateSlidesPerView();
    this.goTo(this.currentIndex, false);
  }

  onPrevClick() {
    this.goTo(this.currentIndex - 1, true);
  }

  onNextClick() {
    this.goTo(this.currentIndex + 1, true);
  }

  goTo(index, animate) {
    if (this.totalSlides === 0) return;

    if (index < 0) {
      index = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      index = 0;
    }

    this.currentIndex = index;

    const slide = this.track.children[0];
    const slideWidth = slide.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--carousel-gap')) || 0;
    const offset = (slideWidth + gap) * index;

    this.track.classList.toggle('is-dragging', !animate);
    this.track.style.transform = `translateX(-${offset}px)`;

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  onPointerDown(event) {
    this.dragStartX = event.clientX;
    this.dragCurrentX = event.clientX;
    this.isDragging = true;
    this.track.classList.add('is-dragging');

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    this.dragCurrentX = event.clientX;

    const slide = this.track.children[0];
    const slideWidth = slide.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--carousel-gap')) || 0;
    const baseOffset = (slideWidth + gap) * this.currentIndex;
    const dragDelta = this.dragCurrentX - this.dragStartX;

    this.track.style.transform = `translateX(${-baseOffset + dragDelta}px)`;
  }

  onPointerUp() {
    if (!this.isDragging) return;
    this.isDragging = false;

    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);

    const dragDelta = this.dragCurrentX - this.dragStartX;
    const threshold = 40;

    if (dragDelta > threshold) {
      this.goTo(this.currentIndex - 1, true);
    } else if (dragDelta < -threshold) {
      this.goTo(this.currentIndex + 1, true);
    } else {
      this.goTo(this.currentIndex, true);
    }
  }
}

customElements.define('carousel-recommendations', CarouselRecommendations);